// Media layer (docs/contracts.md section 4). The renderer and audio engine only see MediaItems.

export interface MediaItem {
  id: string;
  title: string;
  /** One or more audio URLs played together. `synth:<name>` is a locally generated signal. */
  audio: string[];
  /** Optional video shown on the room's screen surfaces. */
  video?: string;
  duration_s?: number;
  /** Poster image for the monitor (contract section 5). */
  poster?: string;
  /** Several posters, crossfaded slowly on the monitor. */
  posters?: string[];
  subtitles?: string[];
  /** Web client only (not in the contract): how the audio engine starts and leaves this item. */
  transition?: Transition;
}

export interface Transition {
  /** Fade-in of the tracks, -60 dB -> 0 dB (exponential), seconds. */
  fade_in_s: number;
  /** Fade-out of this item when another item replaces it, seconds. */
  fade_out_s: number;
  /** Brown noise bridge level (0 = no bridge). It rises over 0.5 s on play / item change. */
  bridge_gain: number;
  /** Bridge fade-out, starting 0.4 s after the tracks start, seconds. */
  bridge_out_s: number;
}

export const DEFAULT_TRANSITION: Transition = { fade_in_s: 0.5, fade_out_s: 2.5, bridge_gain: 0, bridge_out_s: 1 };

export interface MediaSource {
  id: string;
  list(): Promise<MediaItem[]>;
}

/** The generated test signal: always available, so spatial audio can be tested without files. */
export const SYNTH_ITEM: MediaItem = {
  id: 'synth-chord',
  title: 'Test chord loop (synthesized)',
  audio: ['synth:chord'],
  duration_s: 19.2,
};

/**
 * Files in web/public/media/, listed in media/playlist.json ({"items": MediaItem[]}, URLs relative to the
 * site root). Items whose files are missing are dropped. The synthesized chord loop is appended last so
 * there is always something to play.
 */
export class LocalSource implements MediaSource {
  id = 'local';

  async list(): Promise<MediaItem[]> {
    const base = import.meta.env.BASE_URL;
    let items: MediaItem[] = [];
    try {
      const res = await fetch(`${base}media/playlist.json`, { cache: 'no-cache' });
      if (res.ok && (res.headers.get('content-type') || '').includes('json')) {
        const data = (await res.json()) as { items?: MediaItem[] };
        items = (data.items ?? []).filter((i) => i && i.id && Array.isArray(i.audio) && i.audio.length);
      }
    } catch {
      /* no playlist */
    }
    const resolve = (u: string) => (/^(https?:|synth:|blob:|data:)/.test(u) ? u : base + u.replace(/^\//, ''));
    items = items.map((i) => ({ ...i, audio: i.audio.map(resolve), video: i.video ? resolve(i.video) : undefined }));
    const checked = await Promise.all(items.map(async (i) => ((await allExist([...i.audio, ...(i.video ? [i.video] : [])])) ? i : null)));
    const ok = checked.filter((i): i is MediaItem => i !== null);
    return [...ok, SYNTH_ITEM];
  }
}

async function allExist(urls: string[]): Promise<boolean> {
  const r = await Promise.all(
    urls.map(async (u) => {
      if (u.startsWith('synth:')) return true;
      try {
        const res = await fetch(u, { method: 'HEAD' });
        const type = res.headers.get('content-type') || '';
        return res.ok && !type.includes('text/html');
      } catch {
        return false;
      }
    }),
  );
  return r.every(Boolean);
}
